import { useEffect, useState } from 'react';
import aiapi from './aiapi';
import { getApiKey } from './apiKeyProvider';

/**
 * AI Settings 의 켜짐 여부 (CSQ Server GET api/aicenter/settings/flags, 2026-10-06).
 * 앱 시작 때(IfmsMain) 한 번 읽고, 설정 화면에서 저장하면 다시 읽는다.
 *  - aiCenter: false → AI Center 메뉴·목록은 보이되 열지 않고 경고 (eAWB 미사용과 같은 문구)
 *  - dropZone: false → 우측 하단 sparkle 아이콘은 보이되 눌러도 Drop Zone 이 열리지 않음
 * 아직 못 읽었거나 읽기에 실패하면(null) 막지 않는다 — 업로드는 서버도 같은 설정으로 막는다.
 * AI 를 쓰는 회사(AI_ENABLED_TENANTS)가 아니면 서버에 묻지 않고 전부 꺼짐으로 본다 (2026-10-12 — AI 키가 BVL 키 하나로 고정돼
 * 다른 사이트도 BVL 설정·목록을 읽던 것을 막는다. IfmsMain 이 로그인 회사로 setAiTenant 를 부른다).
 */
export interface AiCenterFlags {
  aiCenter: boolean;
  emailIntake: boolean;
  dropZone: boolean;
  message: string;
}

/** 기능이 꺼져 있을 때의 안내 — IfmsMain 의 eAWB 미사용 경고와 같은 문구 */
export const AI_CENTER_CONTACT_SUPPORT = 'Please contact Suntek for Assistance!';

/** AI 를 쓰는 회사(suntekId) — 여기 없는 회사는 AI Settings 가 전부 꺼진 것으로 본다 (2026-10-12) */
// AIBVLG0056 = BVL TEST(로컬 점검용, 2026-10-14) — App.tsx 의 AIBVLG0056 키와 함께 운영 배포 전에 되돌릴 것
const AI_ENABLED_TENANTS = ['BVLG005690', 'AIBVLG0056'];
let aiTenantAllowed = false;
let aiCenterGranted = true;


/** 로그인한 회사가 AI 를 쓰는 회사인지 정한다 — IfmsMain 이 refreshAiCenterFlags 전에 부른다 */
export function setAiTenant(suntekId?: string | null) {
  aiTenantAllowed = AI_ENABLED_TENANTS.includes((suntekId ?? '').trim().toUpperCase());
}

/** 로그인한 사용자의 AI Center 권한 — IfmsMain 이 refreshAiCenterFlags 전에 부른다 */
export function setAiCenterGrant(granted: boolean) {
  aiCenterGranted = granted;
}

export function isAiTenantAllowed(): boolean {
  return aiTenantAllowed;
}

let current: AiCenterFlags | null = null;
const listeners = new Set<(flags: AiCenterFlags | null) => void>();

export function getAiCenterFlags(): AiCenterFlags | null {
  return current;
}

export async function refreshAiCenterFlags(): Promise<AiCenterFlags | null> {
 
  if (!aiTenantAllowed || !aiCenterGranted) {
    // AI 를 쓰지 않는 회사 — 서버에 묻지 않고 전부 꺼짐 (AI Center 목록·Drop Zone 이 열리지 않는다)
    current = { aiCenter: false, emailIntake: false, dropZone: false, message: AI_CENTER_CONTACT_SUPPORT };
    listeners.forEach(listener => listener(current));
    return current;
  }
  try {
    const res = await aiapi.get('api/aicenter/settings/flags', {
      headers: { 'ApiKey': getApiKey(), 'ngrok-skip-browser-warning': 'true' },
    });
    current = res?.data ?? null;
  } catch {
    // 읽지 못하면 이전 값을 유지한다 (처음이면 null — 막지 않음)
  }
  listeners.forEach(listener => listener(current));
  return current;
}

export function useAiCenterFlags(): AiCenterFlags | null {
  const [flags, setFlags] = useState<AiCenterFlags | null>(current);
  useEffect(() => {
    listeners.add(setFlags);
    setFlags(current);
    return () => {
      listeners.delete(setFlags);
    };
  }, []);
  return flags;
}

export function isAiCenterOff(flags: AiCenterFlags | null = current): boolean {
  return flags !== null && flags.aiCenter === false;
}

export function isDropZoneOff(flags: AiCenterFlags | null = current): boolean {
  return flags !== null && flags.dropZone === false;
}
